import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SocialMetaTags from './SocialMetaTags';
import { getPercentageColor } from '../utils/riskUtils';
import { apiFetch, getApiHost } from '../utils/api';

interface NetworkToken {
  mint: string;
  name: string;
  symbol: string;
  uri: string;
  uriMetadata?: any;
  creator: string;
  creatorNetwork: string;
  createdAt: string;
  description: string;
  telegram: string;
  twitter: string;
  website: string;
  decimals?: number;
  networkWalletCount: number;
  networkPercentHeld: number;
  timestamp: string;
  relationship?: 'bundled' | 'created' | 'both';
}

interface NetworkWallet {
  address: string;
  createdAt?: string;
  fundingSource: string;
  exchange?: string;
  label: string | null;
  tags: string[];
  totalNetworkSize: number;
  oldestTx?: string;
  oldestTxSignature?: string;
}

interface NetworkDetails {
  networkName: string;
  isExchange?: boolean;
  exchangeName?: string | null;
  tokens: NetworkToken[];
  totalTokens: number;
  tokensPage: number;
  tokensLimit: number;
  tokensTotalPages: number;
  createdAt: string;
  fundingSource: string;
  fundingTxSignature?: string;
  totalNetworkSize: number;
  exchange: string;
  fundedWalletCount: number;
  tokensCreatedCount?: number;
  tokensBundledCount?: number;
}

type TabView = 'tokens' | 'created' | 'wallets' | 'directFunded';

function NetworkDetailsPage() {
  const { networkAddress } = useParams<{ networkAddress: string }>();
  const [tokens, setTokens] = useState<NetworkToken[]>([]);
  const [networkName, setNetworkName] = useState<string | null>(null);
  const [networkDetails, setNetworkDetails] = useState<NetworkDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokensPage, setTokensPage] = useState(1);
  const [totalTokens, setTotalTokens] = useState(0);
  const [tokensTotalPages, setTokensTotalPages] = useState(0);
  const TOKENS_PER_PAGE = 20;

  // Wallets tab state
  const [activeTab, setActiveTab] = useState<TabView>('tokens');
  const [wallets, setWallets] = useState<NetworkWallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletsError, setWalletsError] = useState<string | null>(null);
  const [walletsPage, setWalletsPage] = useState(1);
  const [walletsTotal, setWalletsTotal] = useState(0);
  const [walletsTotalPages, setWalletsTotalPages] = useState(0);
  const [walletsLoaded, setWalletsLoaded] = useState(false);
  const WALLETS_PER_PAGE = 50;

  // Created tokens tab state
  const [createdTokens, setCreatedTokens] = useState<NetworkToken[]>([]);
  const [createdLoading, setCreatedLoading] = useState(false);
  const [createdPage, setCreatedPage] = useState(1);
  const [totalCreated, setTotalCreated] = useState(0);
  const [createdTotalPages, setCreatedTotalPages] = useState(0);
  const [createdLoaded, setCreatedLoaded] = useState(false);

  // Direct funded wallets tab state
  const [directFunded, setDirectFunded] = useState<NetworkWallet[]>([]);
  const [directFundedLoading, setDirectFundedLoading] = useState(false);
  const [directFundedError, setDirectFundedError] = useState<string | null>(null);
  const [directFundedPage, setDirectFundedPage] = useState(1);
  const [directFundedTotal, setDirectFundedTotal] = useState(0);
  const [directFundedTotalPages, setDirectFundedTotalPages] = useState(0);
  const [directFundedLoaded, setDirectFundedLoaded] = useState(false);

  useEffect(() => {
    if (networkAddress) {
      fetchNetworkTokens(networkAddress, 1);
    }
  }, [networkAddress]);

  useEffect(() => {
    if (activeTab === 'created' && networkAddress && !createdLoaded) {
      fetchCreatedTokens(networkAddress, 1);
    }
    if (activeTab === 'wallets' && networkAddress && !walletsLoaded) {
      fetchNetworkWallets(networkAddress, 1);
    }
    if (activeTab === 'directFunded' && networkAddress && !directFundedLoaded) {
      fetchDirectFundedWallets(networkAddress, 1);
    }
  }, [activeTab, networkAddress, createdLoaded, walletsLoaded, directFundedLoaded]);

  const fetchNetworkTokens = async (address: string, page: number) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setTokensLoading(true);
      }
      const response = await apiFetch(`${getApiHost()}/api/network-details/${address}?page=${page}&limit=${TOKENS_PER_PAGE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch network details');
      }
      const data = await response.json();
      setTokens(data.tokens || []);
      setTotalTokens(data.totalTokens || 0);
      setTokensPage(data.tokensPage || 1);
      setTokensTotalPages(data.tokensTotalPages || 0);
      setNetworkName(data.networkName || null);
      setNetworkDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setTokensLoading(false);
    }
  };

  const handleTokensPageChange = (newPage: number) => {
    if (networkAddress && newPage >= 1 && newPage <= tokensTotalPages) {
      fetchNetworkTokens(networkAddress, newPage);
    }
  };

  const fetchCreatedTokens = async (address: string, page: number) => {
    try {
      setCreatedLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/network-details/${address}?tab=created&page=${page}&limit=${TOKENS_PER_PAGE}`);
      if (!response.ok) throw new Error('Failed to fetch created tokens');
      const data = await response.json();
      setCreatedTokens(data.tokens || []);
      setTotalCreated(data.totalTokens || 0);
      setCreatedPage(data.tokensPage || 1);
      setCreatedTotalPages(data.tokensTotalPages || 0);
      setCreatedLoaded(true);
    } catch (err) {
      // silently fail
    } finally {
      setCreatedLoading(false);
    }
  };

  const handleCreatedPageChange = (newPage: number) => {
    if (networkAddress && newPage >= 1 && newPage <= createdTotalPages) {
      fetchCreatedTokens(networkAddress, newPage);
    }
  };

  const fetchNetworkWallets = async (address: string, page: number) => {
    try {
      setWalletsLoading(true);
      setWalletsError(null);
      const response = await apiFetch(
        `${getApiHost()}/api/network-details/${address}/wallets?page=${page}&limit=${WALLETS_PER_PAGE}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch network wallets');
      }
      const data = await response.json();
      setWallets(data.wallets || []);
      setWalletsTotal(data.total || 0);
      setWalletsTotalPages(data.totalPages || 0);
      setWalletsPage(data.page || 1);
      setWalletsLoaded(true);
    } catch (err) {
      setWalletsError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setWalletsLoading(false);
    }
  };

  const handleWalletsPageChange = (newPage: number) => {
    if (networkAddress && newPage >= 1 && newPage <= walletsTotalPages) {
      fetchNetworkWallets(networkAddress, newPage);
    }
  };

  const fetchDirectFundedWallets = async (address: string, page: number) => {
    try {
      setDirectFundedLoading(true);
      setDirectFundedError(null);
      const response = await apiFetch(
        `${getApiHost()}/api/wallet-details/${address}/children?page=${page}&limit=${WALLETS_PER_PAGE}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch directly funded wallets');
      }
      const data = await response.json();
      setDirectFunded(data.wallets || []);
      setDirectFundedTotal(data.total || 0);
      setDirectFundedTotalPages(data.totalPages || 0);
      setDirectFundedPage(data.page || 1);
      setDirectFundedLoaded(true);
    } catch (err) {
      setDirectFundedError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDirectFundedLoading(false);
    }
  };

  const handleDirectFundedPageChange = (newPage: number) => {
    if (networkAddress && newPage >= 1 && newPage <= directFundedTotalPages) {
      fetchDirectFundedWallets(networkAddress, newPage);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    let date: Date;

    // Try parsing as ISO string first (for createdAt)
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      date = isoDate;
    } else {
      // Try parsing as epoch timestamp (for analysis timestamps)
      const timestamp = parseInt(dateString);
      date = !isNaN(timestamp) && timestamp > 0 ? new Date(timestamp) : new Date();
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ago`;
    } else {
      return 'now';
    }
  };

  const formatAbsoluteDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US');
  };

  const getTokenImage = (token: NetworkToken) => {
    if (token.uriMetadata?.image) {
      return token.uriMetadata.image;
    }
    // Generate SVG placeholder with token symbol
    const symbol = (token.symbol || '?').substring(0, 4);
    const svg = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="24" fill="#374151"/>
      <text x="24" y="28" text-anchor="middle" fill="#9CA3AF" font-size="14" font-weight="600" font-family="system-ui, -apple-system, sans-serif">${symbol}</text>
    </svg>`;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="text-gray-400">Loading network details...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="text-red-400 mb-4">
              <i className="fas fa-exclamation-triangle text-4xl"></i>
            </div>
            <p className="text-gray-400 mb-4">Failed to load network details: {error}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageUrl = `${window.location.origin}/network/${networkAddress}`;
  const title = `Network ${networkAddress?.slice(0, 4)}...${networkAddress?.slice(-4)} - Token Analysis | Deepnets.ai`;
  const description = `View tokens where network ${networkAddress?.slice(0, 4)}...${networkAddress?.slice(-4)} is the top network bundler.`;

  // Exchange / bridge addresses are NOT wallet networks — thousands of unrelated
  // wallets are merely funded THROUGH them. Show a plain exchange notice instead
  // of the (misleading) network graph, tabs, and wallet lists.
  if (networkDetails?.isExchange) {
    const exName = networkDetails.exchangeName || networkName || 'Known exchange';
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <SocialMetaTags title={`${exName} (exchange) | Deepnets.ai`} description={`${exName} is a known exchange/bridge, not a wallet network.`} image={`${window.location.origin}/logo192.png`} url={pageUrl} type="article" />
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-8">
          <h1 className="text-3xl font-bold mb-4">Exchange / Bridge</h1>
          <div className="bg-crypto-card rounded-lg p-6 border border-gray-700">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-building-columns text-crypto-accent text-lg"></i>
              <span className="text-xl font-semibold">{exName}</span>
              <span className="text-[11px] uppercase tracking-wide text-crypto-accent border border-crypto-accent/40 rounded px-1.5 py-0.5">Exchange</span>
            </div>
            <p className="text-gray-300 leading-relaxed mb-5">
              This address is a known exchange / bridge, not a coordinated wallet network.
              Many unrelated wallets are simply funded <em>through</em> it, so it is not grouped
              or scored as a bundler network, and the wallets it funds are treated as independent.
            </p>
            <div className="text-sm text-gray-400 mb-1">Address</div>
            <a
              href={`https://solscan.io/account/${networkAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-crypto-accent hover:text-crypto-accent/80 transition-colors break-all"
            >
              {networkAddress}
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <SocialMetaTags
        title={title}
        description={description}
        image={`${window.location.origin}/logo192.png`}
        url={pageUrl}
        type="article"
      />
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Network Details</h1>
          <div className="bg-crypto-card rounded-lg p-6 border border-gray-700 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-400 mb-1">Top Funding Wallet</div>
                <a
                  href={`https://solscan.io/account/${networkAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-crypto-accent hover:text-crypto-accent/80 transition-colors break-all"
                >
                  {networkAddress}
                </a>
              </div>
              {networkName && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Network Name (derived from address)</div>
                  <div className="flex items-center space-x-2 text-white">
                    <i className="fas fa-key text-crypto-accent"></i>
                    <span className="font-medium">{networkName}</span>
                  </div>
                </div>
              )}
              {networkDetails?.totalNetworkSize && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Total Network Size</div>
                  <div className="text-white font-semibold">
                    {formatNumber(networkDetails.totalNetworkSize)} wallets
                  </div>
                </div>
              )}
              {networkDetails?.fundedWalletCount && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Direct Funded Wallets</div>
                  <div className="text-white font-semibold">
                    {formatNumber(networkDetails.fundedWalletCount)} wallets
                  </div>
                </div>
              )}
              {networkDetails?.tokensCreatedCount != null && networkDetails.tokensCreatedCount > 0 && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Tokens Created</div>
                  <div className="text-white font-semibold">
                    {formatNumber(networkDetails.tokensCreatedCount)} tokens
                  </div>
                </div>
              )}
              {networkDetails?.exchange && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Original Funding Exchange</div>
                  <div className="text-white font-semibold">
                    {networkDetails.exchange}
                  </div>
                </div>
              )}
              {networkDetails?.fundingTxSignature && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Original Funding Tx</div>
                  <div className="text-white font-semibold">
                    <a
                      href={`https://solscan.io/tx/${networkDetails.fundingTxSignature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-400 transition-colors"
                    >
                      {networkDetails.fundingTxSignature.slice(0, 6)}...{networkDetails.fundingTxSignature.slice(-4)}
                      <i className="fas fa-external-link-alt ml-1.5 text-xs text-gray-400"></i>
                    </a>
                  </div>
                </div>
              )}
              {networkDetails?.createdAt && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Network Created</div>
                  <div className="text-white">
                    <div className="font-semibold">{formatAbsoluteDate(networkDetails.createdAt)}</div>
                    <div className="text-sm text-gray-400">{formatTimeAgo(networkDetails.createdAt)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center space-x-1 mb-6 bg-crypto-card rounded-lg p-1 border border-gray-700 w-fit">
          <button
            onClick={() => setActiveTab('tokens')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'tokens'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-coins mr-2"></i>
            Bundled {totalTokens > 0 && <span className="ml-1 text-xs opacity-75">({formatNumber(totalTokens)})</span>}
          </button>
          <button
            onClick={() => setActiveTab('created')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'created'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-paint-brush mr-2"></i>
            Created {networkDetails?.tokensCreatedCount ? <span className="ml-1 text-xs opacity-75">({formatNumber(networkDetails.tokensCreatedCount)})</span> : null}
          </button>
          <button
            onClick={() => setActiveTab('wallets')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'wallets'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-project-diagram mr-2"></i>
            Network Wallets {networkDetails?.totalNetworkSize ? <span className="ml-1 text-xs opacity-75">({formatNumber(networkDetails.totalNetworkSize)})</span> : null}
          </button>
          <button
            onClick={() => setActiveTab('directFunded')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'directFunded'
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-share-alt mr-2"></i>
            Direct Funded {directFundedLoaded && directFundedTotal > 0 ? <span className="ml-1 text-xs opacity-75">({formatNumber(directFundedTotal)})</span> : null}
          </button>
        </div>

        {/* Tokens Tab */}
        {activeTab === 'tokens' && (
          <>
            {tokens.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="fas fa-search text-4xl mb-2"></i>
                  <p className="text-lg">No tokens found for this network</p>
                  <p className="text-sm">This network may not be a top network bundler for any recent tokens.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-gray-400 text-sm">
                    Showing {formatNumber((tokensPage - 1) * TOKENS_PER_PAGE + 1)}–{formatNumber(Math.min(tokensPage * TOKENS_PER_PAGE, totalTokens))} of {formatNumber(totalTokens)} token{totalTokens !== 1 ? 's' : ''} bundled by this network
                  </p>
                  {tokensLoading && <div className="text-gray-500 text-sm">Loading...</div>}
                </div>

                <div className="grid gap-4">
                  {tokens.map((token) => (
                    <div key={token.mint} className="bg-crypto-card rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center space-x-4">
                        <img
                          src={getTokenImage(token)}
                          alt={token.name}
                          className="w-12 h-12 rounded-full border-2 border-gray-700 object-cover flex-shrink-0"
                          onError={(e) => {
                            const symbol = (token.symbol || '?').substring(0, 4);
                            const svg = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="24" fill="#374151"/><text x="24" y="28" text-anchor="middle" fill="#9CA3AF" font-size="14" font-weight="600" font-family="system-ui, -apple-system, sans-serif">${symbol}</text></svg>`;
                            (e.target as HTMLImageElement).src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
                          }}
                        />
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center space-x-2 mb-1 flex-wrap">
                            <a
                              href={`/holder-analysis/${token.mint}/latest`}
                              className="text-2xl font-bold text-white hover:text-crypto-accent transition-colors"
                            >
                              {token.symbol || '???'}
                            </a>
                            <a
                              href={`/holder-analysis/${token.mint}/latest`}
                              className="text-sm text-gray-300 font-medium truncate hover:text-crypto-accent transition-colors"
                            >
                              {token.name || 'Unknown Token'}
                            </a>
                            {token.relationship === 'both' && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                Also Created
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between text-sm gap-2 md:gap-0">
                            <div className="flex items-center space-x-3 text-gray-400">
                              <span>Token created {formatTimeAgo(token.createdAt)}</span>
                              <span>•</span>
                              <a
                                href={`https://solscan.io/token/${token.mint}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-500 hover:text-gray-300 font-mono transition-colors"
                                title="View on Solscan"
                              >
                                {token.mint.slice(0, 4)}...{token.mint.slice(-4)}
                              </a>
                              <button
                                onClick={() => navigator.clipboard.writeText(token.mint)}
                                className="text-crypto-accent hover:text-crypto-accent/80 transition-colors px-1"
                                title="Copy mint address"
                              >
                                <i className="fas fa-copy text-xs"></i>
                              </button>
                            </div>
                            <div className="flex items-center justify-between md:justify-end space-x-4">
                              <div className="flex items-center space-x-2">
                                <a
                                  href={`https://pump.fun/${token.mint}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="View on Pump.fun"
                                  className="opacity-60 hover:opacity-100 transition-opacity"
                                >
                                  <img src="/pumpfun.png" alt="PumpFun" className="w-4 h-4" />
                                </a>
                                <a
                                  href={`https://gmgn.ai/sol/token/${process.env.REACT_APP_GMGN_REFERRAL_CODE}_${token.mint}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="View on GMGN"
                                  className="opacity-60 hover:opacity-100 transition-opacity"
                                >
                                  <img src="/gmgn.svg" alt="GMGN" className="w-4 h-4" />
                                </a>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <div className={`text-lg font-bold ${getPercentageColor(token.networkPercentHeld)}`}>
                                    {token.networkPercentHeld.toFixed(1)}%
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {token.networkWalletCount} wallet{token.networkWalletCount !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Token Pagination */}
                {tokensTotalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-6">
                    <button
                      onClick={() => handleTokensPageChange(1)}
                      disabled={tokensPage === 1 || tokensLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      onClick={() => handleTokensPageChange(tokensPage - 1)}
                      disabled={tokensPage === 1 || tokensLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-300">
                      Page {tokensPage} of {formatNumber(tokensTotalPages)}
                    </span>
                    <button
                      onClick={() => handleTokensPageChange(tokensPage + 1)}
                      disabled={tokensPage === tokensTotalPages || tokensLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      onClick={() => handleTokensPageChange(tokensTotalPages)}
                      disabled={tokensPage === tokensTotalPages || tokensLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Created Tokens Tab */}
        {activeTab === 'created' && (
          <>
            {createdLoading && !createdLoaded ? (
              <div className="text-center py-12">
                <div className="text-gray-400">Loading created tokens...</div>
              </div>
            ) : createdTokens.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="fas fa-paint-brush text-4xl mb-2"></i>
                  <p className="text-lg">No tokens created by this network</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-gray-400 text-sm">
                    Showing {formatNumber((createdPage - 1) * TOKENS_PER_PAGE + 1)}–{formatNumber(Math.min(createdPage * TOKENS_PER_PAGE, totalCreated))} of {formatNumber(totalCreated)} token{totalCreated !== 1 ? 's' : ''} created by this network
                  </p>
                  {createdLoading && <div className="text-gray-500 text-sm">Loading...</div>}
                </div>

                <div className="grid gap-4">
                  {createdTokens.map((token) => (
                    <div key={token.mint} className="bg-crypto-card rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center space-x-4">
                        <img
                          src={getTokenImage(token)}
                          alt={token.name}
                          className="w-12 h-12 rounded-full border-2 border-gray-700 object-cover flex-shrink-0"
                          onError={(e) => {
                            const symbol = (token.symbol || '?').substring(0, 4);
                            const svg = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="24" fill="#374151"/><text x="24" y="28" text-anchor="middle" fill="#9CA3AF" font-size="14" font-weight="600" font-family="system-ui, -apple-system, sans-serif">${symbol}</text></svg>`;
                            (e.target as HTMLImageElement).src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
                          }}
                        />
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center space-x-2 mb-1 flex-wrap">
                            <a
                              href={`/holder-analysis/${token.mint}/latest`}
                              className="text-2xl font-bold text-white hover:text-crypto-accent transition-colors"
                            >
                              {token.symbol || '???'}
                            </a>
                            <a
                              href={`/holder-analysis/${token.mint}/latest`}
                              className="text-sm text-gray-300 font-medium truncate hover:text-crypto-accent transition-colors"
                            >
                              {token.name || 'Unknown Token'}
                            </a>
                            {token.relationship === 'both' && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                                Also Bundled
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3 text-sm text-gray-400">
                            <span>Created {formatTimeAgo(token.createdAt)}</span>
                            <span>•</span>
                            <a
                              href={`https://solscan.io/token/${token.mint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-500 hover:text-gray-300 font-mono transition-colors"
                            >
                              {token.mint.slice(0, 4)}...{token.mint.slice(-4)}
                            </a>
                            <button
                              onClick={() => navigator.clipboard.writeText(token.mint)}
                              className="text-crypto-accent hover:text-crypto-accent/80 transition-colors px-1"
                              title="Copy mint address"
                            >
                              <i className="fas fa-copy text-xs"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {createdTotalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-6">
                    <button
                      onClick={() => handleCreatedPageChange(1)}
                      disabled={createdPage === 1 || createdLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      onClick={() => handleCreatedPageChange(createdPage - 1)}
                      disabled={createdPage === 1 || createdLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-300">
                      Page {createdPage} of {formatNumber(createdTotalPages)}
                    </span>
                    <button
                      onClick={() => handleCreatedPageChange(createdPage + 1)}
                      disabled={createdPage === createdTotalPages || createdLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      onClick={() => handleCreatedPageChange(createdTotalPages)}
                      disabled={createdPage === createdTotalPages || createdLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Wallets Tab */}
        {activeTab === 'wallets' && (
          <>
            {walletsLoading && !walletsLoaded ? (
              <div className="text-center py-12">
                <div className="text-gray-400">Loading wallets...</div>
              </div>
            ) : walletsError ? (
              <div className="text-center py-12">
                <div className="text-red-400 mb-4">
                  <i className="fas fa-exclamation-triangle text-4xl"></i>
                </div>
                <p className="text-gray-400">Failed to load wallets: {walletsError}</p>
              </div>
            ) : wallets.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="fas fa-wallet text-4xl mb-2"></i>
                  <p className="text-lg">No wallets found for this network</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-gray-400 text-sm">
                    Showing {formatNumber((walletsPage - 1) * WALLETS_PER_PAGE + 1)}–{formatNumber(Math.min(walletsPage * WALLETS_PER_PAGE, walletsTotal))} of {formatNumber(walletsTotal)} wallets in this network
                  </p>
                  {walletsLoading && <div className="text-gray-500 text-sm">Loading...</div>}
                </div>

                <div className="bg-crypto-card border border-gray-700 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-crypto-dark border-b border-gray-700">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet Address</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet Age</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400 hidden md:table-cell">Funding Tx</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400 hidden lg:table-cell">Funding Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {wallets.map((wallet) => (
                          <tr key={wallet.address} className="hover:bg-crypto-dark/50 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center space-x-2">
                                <Link
                                  to={`/wallet/${wallet.address}`}
                                  className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                >
                                  {wallet.label || `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`}
                                </Link>
                                <button
                                  onClick={() => navigator.clipboard.writeText(wallet.address)}
                                  className="text-gray-400 hover:text-white transition-colors"
                                >
                                  <i className="fas fa-copy text-xs"></i>
                                </button>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-white font-medium text-sm">
                                {wallet.oldestTx ? formatTimeAgo(wallet.oldestTx) : '—'}
                              </span>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              {wallet.oldestTxSignature ? (
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={`https://solscan.io/tx/${wallet.oldestTxSignature}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                  >
                                    {wallet.oldestTxSignature.slice(0, 6)}...{wallet.oldestTxSignature.slice(-4)}
                                  </a>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(wallet.oldestTxSignature!)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <i className="fas fa-copy text-xs"></i>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">—</span>
                              )}
                            </td>
                            <td className="p-4 hidden lg:table-cell">
                              {wallet.fundingSource ? (
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={`https://solscan.io/account/${wallet.fundingSource}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                  >
                                    {wallet.fundingSource.slice(0, 6)}...{wallet.fundingSource.slice(-4)}
                                  </a>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(wallet.fundingSource)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <i className="fas fa-copy text-xs"></i>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">Unknown</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {walletsTotalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-6">
                    <button
                      onClick={() => handleWalletsPageChange(1)}
                      disabled={walletsPage === 1 || walletsLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      onClick={() => handleWalletsPageChange(walletsPage - 1)}
                      disabled={walletsPage === 1 || walletsLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-300">
                      Page {walletsPage} of {formatNumber(walletsTotalPages)}
                    </span>
                    <button
                      onClick={() => handleWalletsPageChange(walletsPage + 1)}
                      disabled={walletsPage === walletsTotalPages || walletsLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      onClick={() => handleWalletsPageChange(walletsTotalPages)}
                      disabled={walletsPage === walletsTotalPages || walletsLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Direct Funded Wallets Tab */}
        {activeTab === 'directFunded' && (
          <>
            {directFundedLoading && !directFundedLoaded ? (
              <div className="text-center py-12">
                <div className="text-gray-400">Loading directly funded wallets...</div>
              </div>
            ) : directFundedError ? (
              <div className="text-center py-12">
                <div className="text-red-400 mb-4">
                  <i className="fas fa-exclamation-triangle text-4xl"></i>
                </div>
                <p className="text-gray-400">Failed to load wallets: {directFundedError}</p>
              </div>
            ) : directFunded.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="fas fa-share-alt text-4xl mb-2"></i>
                  <p className="text-lg">No directly funded wallets found</p>
                  <p className="text-sm">This wallet hasn't directly funded any other wallets.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-gray-400 text-sm">
                    Showing {formatNumber((directFundedPage - 1) * WALLETS_PER_PAGE + 1)}–{formatNumber(Math.min(directFundedPage * WALLETS_PER_PAGE, directFundedTotal))} of {formatNumber(directFundedTotal)} directly funded wallets
                  </p>
                  {directFundedLoading && <div className="text-gray-500 text-sm">Loading...</div>}
                </div>

                <div className="bg-crypto-card border border-gray-700 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-crypto-dark border-b border-gray-700">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet Address</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet Age</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400 hidden md:table-cell">Funding Tx</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-400 hidden lg:table-cell">Tags</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {directFunded.map((wallet: any) => (
                          <tr key={wallet.address} className="hover:bg-crypto-dark/50 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center space-x-2">
                                <Link
                                  to={`/wallet/${wallet.address}`}
                                  className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                >
                                  {wallet.label || `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`}
                                </Link>
                                <button
                                  onClick={() => navigator.clipboard.writeText(wallet.address)}
                                  className="text-gray-400 hover:text-white transition-colors"
                                >
                                  <i className="fas fa-copy text-xs"></i>
                                </button>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-white font-medium text-sm">
                                {wallet.oldestTx ? formatTimeAgo(wallet.oldestTx) : '—'}
                              </span>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              {wallet.oldestTxSignature ? (
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={`https://solscan.io/tx/${wallet.oldestTxSignature}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                  >
                                    {wallet.oldestTxSignature.slice(0, 6)}...{wallet.oldestTxSignature.slice(-4)}
                                  </a>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(wallet.oldestTxSignature!)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <i className="fas fa-copy text-xs"></i>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">—</span>
                              )}
                            </td>
                            <td className="p-4 hidden lg:table-cell">
                              {wallet.tags && wallet.tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {wallet.tags.map((tag: string) => (
                                    <span key={tag} className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded-full">{tag}</span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {directFundedTotalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-6">
                    <button
                      onClick={() => handleDirectFundedPageChange(1)}
                      disabled={directFundedPage === 1 || directFundedLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      onClick={() => handleDirectFundedPageChange(directFundedPage - 1)}
                      disabled={directFundedPage === 1 || directFundedLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-300">
                      Page {directFundedPage} of {formatNumber(directFundedTotalPages)}
                    </span>
                    <button
                      onClick={() => handleDirectFundedPageChange(directFundedPage + 1)}
                      disabled={directFundedPage === directFundedTotalPages || directFundedLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      onClick={() => handleDirectFundedPageChange(directFundedTotalPages)}
                      disabled={directFundedPage === directFundedTotalPages || directFundedLoading}
                      className="px-3 py-2 rounded-md text-sm bg-crypto-card border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default NetworkDetailsPage;
